import { Link, Navigate, useLocation } from 'react-router-dom';
import Footer from '../Layouts/Footer';
import Newsletter from '../Homepage/Newsletter';
import Canonical from '../../seo/Canonical';
import RelatedLinks from '../Common/RelatedLinks';
import { getMultiChannelIndustryByPath } from '../../data/multiChannelIndustries';
import { Helmet } from 'react-helmet-async';
import FAQSection from '../FAQSection';
import './MultiChannelIndustryPage.css';

export default function MultiChannelIndustryPage() {
  const { pathname } = useLocation();
  const industry = getMultiChannelIndustryByPath(pathname);

  if (!industry) {
    return <Navigate to="/multi-channel-fulfillment-services" replace />;
  }

  return (
    <div className="mcf-industry-page">
      <Helmet>
        <script type="application/ld+json" nonce="ppp-2026-gtm-nonce">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "headline": industry.pageTitle,
            "description": industry.metaDescription,
            "publisher": {
              "@type": "Organization",
              "name": "Pick Pack Pro",
              "url": "https://www.pickpackpro.co.uk/"
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://www.pickpackpro.co.uk${industry.path}`
            }
          })}
        </script>
      </Helmet>
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <section className="mcf-industry-hero" aria-labelledby="mcf-industry-title">
        <div className="mcf-industry-hero-inner">
          <p className="mcf-industry-eyebrow">{industry.searchPhrase}</p>
          <h1 id="mcf-industry-title">{industry.pageTitle}</h1>
          <p className="mcf-industry-lead">{industry.heroLead}</p>
          <ul className="mcf-industry-bullets">
            {industry.bullets.map((bullet) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </ul>
          <div className="mcf-industry-cta-row">
            <Link to="/contact-fulfillment-services" className="mcf-industry-cta mcf-industry-cta-primary">
              Get a free quote
            </Link>
            <Link
              to="/multi-channel-fulfillment-services"
              className="mcf-industry-cta mcf-industry-cta-secondary"
            >
              Multi-channel fulfilment overview
            </Link>
          </div>
        </div>
      </section>

      {industry.aeoSummary && (
        <section style={{ maxWidth: '800px', margin: '4rem auto 0', padding: '0 1.5rem' }}>
          <div className="home-hero-aeo-summary" style={{ background: '#f8fafc', borderLeft: '4px solid #f58030', border: '1px solid #e2e8f0', padding: '1.5rem', borderRadius: '8px', fontSize: '1rem', lineHeight: '1.6', color: '#1a202c' }}>
            <strong style={{ display: 'block', marginBottom: '0.75rem', color: '#2d3748', fontSize: '1.15rem' }}>Executive Summary</strong>
            <p style={{ margin: '0 0 1rem 0' }}><strong>Who this is for & Use Cases:</strong> {industry.aeoSummary.audience}</p>
            <p style={{ margin: 0 }}><strong>Our Original Experience & Real Data:</strong> {industry.aeoSummary.experience}</p>
          </div>
        </section>
      )}

      {industry.sections?.length ? (
        <section className="mcf-industry-body" aria-label={`${industry.pageTitle} details`}>
          <div className="mcf-industry-body-inner">
            {industry.sections.map((section) => (
              <article key={section.title} className="mcf-industry-section">
                <h2>{section.title}</h2>
                {section.isQuestion && (
                  <p style={{ display: 'none' }}><strong>Answer: </strong> {section.answerPreview || 'Read below for the complete answer.'}</p>
                )}
                <div dangerouslySetInnerHTML={{ __html: section.body }} />
              </article>
            ))}
            <p className="mcf-industry-body-links">
              Learn more about our <Link to="/warehousing-services">secure storage</Link>,{' '}
              <Link to="/return-handling-services">returns processing</Link>, or{' '}
              <Link to="/amazon-fba-prep-services">Amazon FBA prep</Link>.
            </p>
          </div>
        </section>
      ) : null}

      {industry.faqs && industry.faqs.length > 0 && (
        <FAQSection
          headingId="industry-faq"
          className="faqs-section"
          title="Frequently Asked Questions"
          faqs={industry.faqs as any}
        />
      )}

      <RelatedLinks
        title="More industries we support"
        serviceLinks={[
          { to: '/fashion-apparel-fulfilment-uk', label: 'Fashion & apparel fulfilment' },
          { to: '/health-supplements-fulfilment-uk', label: 'Health & supplements' },
          { to: '/electronics-fulfilment-uk', label: 'Electronics fulfilment' },
          { to: '/food-drink-fulfilment-uk', label: 'Food & drink fulfilment' },
          { to: '/tiktok-brands-fulfilment-uk', label: 'TikTok live sales' },
        ]}
      />

      <Newsletter />
      <Footer />
    </div>
  );
}
